import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Badge, type BadgeAccent } from '../primitives/Badge';
import { Button } from '../primitives/Button';

export interface ShopProductCardProps {
  label: string;
  description: string;
  /** Already formatted, e.g. "13,49 €". */
  priceLabel: string;
  /** e.g. "Bester Wert", "Beliebt", "Einmaliges Angebot". */
  badge?: string;
  badgeAccent?: BadgeAccent;
  /** e.g. "+34 % mehr Kristalle pro Euro". Omit for products without a rate to compare. */
  bonusLabel?: string;
  /** One or more composed sprite icons — the card's hero art. */
  art: ReactNode;
  /** Extra glow border — reserve for the one or two cards meant to catch the eye first. */
  featured?: boolean;
  disabled?: boolean;
  /** Shown instead of the buy button when disabled, e.g. "Bereits eingelöst". */
  disabledReason?: string;
  onBuy: () => void;
  className?: string;
}

/**
 * One purchasable product in the Chronos-Shop.
 *
 * Deliberately louder than `EntityCard`: a store card's whole job is to get
 * looked at and clicked, so featured tiers get a glow border and every card
 * leads with a badge/bonus line before the fine print — the opposite of
 * `EntityCard`'s "state first" hierarchy, which is right for buildings but
 * wrong for a shelf of things to buy.
 */
export function ShopProductCard({
  label,
  description,
  priceLabel,
  badge,
  badgeAccent = 'chronos',
  bonusLabel,
  art,
  featured = false,
  disabled = false,
  disabledReason,
  onBuy,
  className,
}: ShopProductCardProps) {
  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-panel border bg-plate shadow-panel',
        'transition-colors duration-[var(--sg-duration)]',
        featured
          ? 'border-chronos/50 shadow-glow-chronos hover:border-chronos/70'
          : 'border-edge hover:border-edge-strong',
        disabled && 'opacity-70',
        className,
      )}
    >
      {badge ? (
        <Badge
          accent={badgeAccent}
          variant="solid"
          size="sm"
          className="absolute left-2 top-2 z-10"
        >
          {badge}
        </Badge>
      ) : null}

      <div className="relative flex aspect-[16/10] items-center justify-center overflow-hidden border-b border-edge-subtle bg-surface-base">
        <div className="absolute inset-0 bg-grid bg-grid-sm opacity-40" aria-hidden />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 70% at 50% 45%, rgb(var(--sg-chronos) / 0.14) 0%, transparent 70%)',
          }}
        />
        <div className="relative flex items-center gap-1.5">{art}</div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <h4 className="font-display text-xs font-semibold uppercase tracking-[0.1em] text-content">
            {label}
          </h4>
          <p className="mt-1 text-xs leading-relaxed text-content-muted">{description}</p>
        </div>

        {bonusLabel ? (
          <div className="text-label font-semibold text-success">{bonusLabel}</div>
        ) : null}

        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span data-sg-numeric className="text-readout font-semibold text-content">
            {priceLabel}
          </span>
          {disabled ? (
            <span className="text-label text-content-muted">{disabledReason}</span>
          ) : (
            <Button variant="premium" size="sm" onClick={onBuy}>
              Kaufen
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
